import { authSelectors } from 'ducks/auth'
import { orgsActions, orgSelectors } from 'ducks/orgs'
import { StockLocationType } from 'opensolar-common/src/services/types/hardware'
import { useLogoutIfAccessDenied } from 'ra-core'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CustomFormUsageType } from 'resources/customForms/types'
import restClient from 'restClient'
import { RoleType } from 'types/roles'
import { useFeatureFlag } from 'util/featureFlags'

type SellerIntegrationResponseType = {
  name: string
  countries: string[]
  is_enabled: boolean
  is_connected: boolean
  is_default: boolean
  filter_key: string
  logo_url: string
  description: string
  privacy_policy_url: string
  terms_of_use_url: string
  account_manager: RoleType | null
  currency_symbol: string
  is_quote_review_enabled: boolean
  stock_locations?: StockLocationType[]
  preferred_stock_location?: string
  custom_forms?: CustomFormUsageType[]
  integration_type?: string
  allow_order_collection?: boolean
}

export const fetchSellerIntegrations = async (
  url: string,
  API_URL: string,
  logoutIfAccessDenied?: (error: any) => void,
  signal?: AbortSignal
) => {
  const restClientInstance = restClient(API_URL)
  try {
    const response = await restClientInstance('CUSTOM_GET', 'custom', {
      url,
      signal,
    })
    return response.data.map((sellerIntegration: SellerIntegrationResponseType) => ({
      name: sellerIntegration.name,
      countries: sellerIntegration.countries,
      isEnabled: sellerIntegration.is_enabled,
      isConnected: sellerIntegration.is_connected,
      isDefault: sellerIntegration.is_default,
      filterKey: sellerIntegration.filter_key,
      logoUrl: sellerIntegration.logo_url,
      description: sellerIntegration.description,
      privacyPolicyUrl: sellerIntegration.privacy_policy_url,
      termsOfUseUrl: sellerIntegration.terms_of_use_url,
      accountManager: sellerIntegration.account_manager,
      currencySymbol: sellerIntegration.currency_symbol,
      isQuoteReviewEnabled: sellerIntegration.is_quote_review_enabled,
      stockLocations: sellerIntegration.stock_locations,
      preferredStockLocation: sellerIntegration.preferred_stock_location,
      customForms: sellerIntegration.custom_forms,
      integrationType: sellerIntegration.integration_type,
      allowsOrderCollection: sellerIntegration.allow_order_collection,
    }))
  } catch (error: any) {
    if (error.name === 'AbortError') return []
    if (!error.body?.mfa_status) console.warn('Failed to load seller integrations data: ', error)
    if (logoutIfAccessDenied) logoutIfAccessDenied(error)
    return []
  }
}

const InitSellerIntegrations: React.FC<{ API_URL: string }> = ({ API_URL }) => {
  const dispatch = useDispatch()
  const orgId = useSelector(orgSelectors.getOrg)?.id
  const org = useSelector(orgSelectors.getOrg)
  const isAuthenticatedPro = useSelector(authSelectors.getIsAuthenticatedPro)
  const enableMultiDistributorShop = useFeatureFlag('enable_multi_distributor_shop', 'on')
  const logoutIfAccessDenied = useLogoutIfAccessDenied()

  const sellerIntegrationsUrl = useMemo(() => {
    if (!orgId || !isAuthenticatedPro) return
    const baseUrl = `orgs/${orgId}`
    if (enableMultiDistributorShop) {
      return `${baseUrl}/available_hardware_distributors/`
    }
    return `${baseUrl}/seller_integrations/?is_enabled=true`
  }, [orgId, enableMultiDistributorShop, isAuthenticatedPro])

  const initializeSellerIntegrations = useCallback(
    async (signal: AbortSignal) => {
      if (!sellerIntegrationsUrl) return
      const sellerIntegrations = await fetchSellerIntegrations(
        sellerIntegrationsUrl,
        API_URL,
        logoutIfAccessDenied,
        signal
      )
      dispatch(orgsActions.initSellerIntegrations(sellerIntegrations))
    },
    [sellerIntegrationsUrl]
  )

  useEffect(() => {
    const controller = new AbortController()
    const { signal } = controller
    initializeSellerIntegrations(signal)
    return () => {
      controller.abort()
    }
  }, [initializeSellerIntegrations, org?.['enable_outlet_integration'], org?.country?.iso2])

  return <></>
}

export default InitSellerIntegrations
